import {
  getSemanticResolver,
  type SemanticResolver,
} from "./semantic-relations";
import type { WordPack } from "./types";

type TeamColor = "red" | "blue";
export type CardTeam = TeamColor | "neutral" | "trap";
export type AssignedCard = { word: string; team: CardTeam };
type RandomFn = () => number;

interface AssignmentOptions {
  random?: RandomFn;
  attempts?: number;
  packs?: WordPack[];
  semanticWords?: string[];
}

const STARTING_TEAM_COUNT = 9;
const OTHER_TEAM_COUNT = 8;
const NEUTRAL_COUNT = 7;
const ASSIGNMENT_SEARCH_ATTEMPTS = 420;
const TOP_ASSIGNMENT_POOL_SIZE = 8;
const PICK_FROM_TOP_CANDIDATES = 2;
const MIN_DESIRED_PAIRS_PER_TEAM = 1;
const BONUS_PAIR_THRESHOLD_PER_TEAM = 2;
const SOFT_MAX_RELATED_LINKS_PER_TEAM = 2;
const SOFT_MAX_SAME_FAMILY_PER_TEAM = 2;

interface AssignmentQuality {
  score: number;
}

interface FamilyDistribution {
  red: number;
  blue: number;
  neutral: number;
  trap: number;
}

function shuffleWithRandom<T>(array: T[], random: RandomFn): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function getMaximumPairCountWithResolver(words: string[], semantics: SemanticResolver): number {
  const size = words.length;
  if (size < 2) return 0;

  const canPair: boolean[][] = Array.from({ length: size }, () => Array(size).fill(false));
  for (let i = 0; i < size; i++) {
    for (let j = i + 1; j < size; j++) {
      if (semantics.shares(words[i], words[j])) {
        canPair[i][j] = true;
        canPair[j][i] = true;
      }
    }
  }

  const memo = new Map<number, number>();
  const fullMask = (1 << size) - 1;

  const search = (mask: number): number => {
    if (mask === 0) return 0;
    const cached = memo.get(mask);
    if (cached !== undefined) return cached;

    let first = 0;
    while (((mask >> first) & 1) === 0) first++;

    const withoutFirst = mask & ~(1 << first);
    let best = search(withoutFirst);

    for (let candidate = first + 1; candidate < size; candidate++) {
      if (((withoutFirst >> candidate) & 1) === 0) continue;
      if (!canPair[first][candidate]) continue;
      const remaining = withoutFirst & ~(1 << candidate);
      best = Math.max(best, 1 + search(remaining));
    }

    memo.set(mask, best);
    return best;
  };

  return search(fullMask);
}

function countRelatedLinks(words: string[], semantics: SemanticResolver): number {
  let relatedLinks = 0;
  for (let i = 0; i < words.length; i++) {
    for (let j = i + 1; j < words.length; j++) {
      if (semantics.shares(words[i], words[j])) {
        relatedLinks++;
      }
    }
  }
  return relatedLinks;
}

function getFamilyDistributions(
  cards: AssignedCard[],
  semantics: SemanticResolver
): Map<number, FamilyDistribution> {
  const distributions = new Map<number, FamilyDistribution>();

  for (const card of cards) {
    const familyIds = semantics.getFamilyIds(card.word);
    if (familyIds.length === 0) continue;

    for (const familyId of familyIds) {
      const existing = distributions.get(familyId) ?? {
        red: 0,
        blue: 0,
        neutral: 0,
        trap: 0,
      };
      existing[card.team] += 1;
      distributions.set(familyId, existing);
    }
  }

  return distributions;
}

function evaluateAssignment(
  cards: AssignedCard[],
  startingTeam: TeamColor,
  otherTeam: TeamColor,
  random: RandomFn,
  semantics: SemanticResolver
): AssignmentQuality {
  const startingWords = cards.filter((card) => card.team === startingTeam).map((card) => card.word);
  const otherWords = cards.filter((card) => card.team === otherTeam).map((card) => card.word);
  const trapWord = cards.find((card) => card.team === "trap")?.word ?? "";

  const startingPairs = getMaximumPairCountWithResolver(startingWords, semantics);
  const otherPairs = getMaximumPairCountWithResolver(otherWords, semantics);
  const startingHasTriple = semantics.hasTriple(startingWords);
  const otherHasTriple = semantics.hasTriple(otherWords);
  const trapSynergy = trapWord
    ? Math.max(
      semantics.countRelated(trapWord, startingWords),
      semantics.countRelated(trapWord, otherWords)
    )
    : 0;

  const pairFloor = Math.min(startingPairs, otherPairs);
  const pairTotal = startingPairs + otherPairs;
  const pairGap = Math.abs(startingPairs - otherPairs);
  const triplesCount = Number(startingHasTriple) + Number(otherHasTriple);
  const pairTargetHit =
    startingPairs >= MIN_DESIRED_PAIRS_PER_TEAM &&
    otherPairs >= MIN_DESIRED_PAIRS_PER_TEAM;
  const pairBonusCount =
    Number(startingPairs >= BONUS_PAIR_THRESHOLD_PER_TEAM) +
    Number(otherPairs >= BONUS_PAIR_THRESHOLD_PER_TEAM);
  const pairOverflowPenalty =
    Math.max(0, startingPairs - BONUS_PAIR_THRESHOLD_PER_TEAM) +
    Math.max(0, otherPairs - BONUS_PAIR_THRESHOLD_PER_TEAM);
  const startingRelatedLinks = countRelatedLinks(startingWords, semantics);
  const otherRelatedLinks = countRelatedLinks(otherWords, semantics);
  const relatedLinkOverflow =
    Math.max(0, startingRelatedLinks - SOFT_MAX_RELATED_LINKS_PER_TEAM) +
    Math.max(0, otherRelatedLinks - SOFT_MAX_RELATED_LINKS_PER_TEAM);

  const familyDistributions = getFamilyDistributions(cards, semantics);
  let dominantFamilyPenalty = 0;
  let severeFamilyPenalty = 0;
  let uncoveredHighRiskFamilyPenalty = 0;
  let splitFamilyBonus = 0;
  let trappedRiskFamilyBonus = 0;

  for (const family of familyDistributions.values()) {
    const redCount = family.red;
    const blueCount = family.blue;
    const colorTotal = redCount + blueCount;
    if (colorTotal < 2) continue;

    const teamMax = Math.max(redCount, blueCount);
    const teamMin = Math.min(redCount, blueCount);

    if (redCount >= 1 && blueCount >= 1 && colorTotal >= 3) {
      splitFamilyBonus += 1;
    }

    if (teamMax > SOFT_MAX_SAME_FAMILY_PER_TEAM) {
      const overflow = teamMax - SOFT_MAX_SAME_FAMILY_PER_TEAM;
      severeFamilyPenalty += overflow * overflow;
    }

    if (teamMax >= 3 && teamMin === 0) {
      dominantFamilyPenalty += teamMax - 2;
    }

    if (teamMax >= 4) {
      if (family.trap > 0) {
        trappedRiskFamilyBonus += 1;
      } else {
        uncoveredHighRiskFamilyPenalty += 1;
      }
    }
  }

  let score =
    pairFloor * 20 +
    Math.min(pairTotal, 4) * 6 -
    pairGap * 12 +
    (pairTargetHit ? 10 : 0) +
    pairBonusCount * 2 +
    splitFamilyBonus * 10 +
    trappedRiskFamilyBonus * 12 -
    triplesCount * 10 -
    pairOverflowPenalty * 18 -
    dominantFamilyPenalty * 16 -
    severeFamilyPenalty * 34 -
    uncoveredHighRiskFamilyPenalty * 18 -
    relatedLinkOverflow * 14;

  // Prefer trap near at least one semantic cluster of 2+ words.
  if (trapSynergy >= 2) {
    score += 12;
  } else if (trapSynergy === 1) {
    score += 3;
  }

  score += random();
  return { score };
}

function createTeamTemplate(startingTeam: TeamColor): CardTeam[] {
  const otherTeam: TeamColor = startingTeam === "red" ? "blue" : "red";
  return [
    ...Array(STARTING_TEAM_COUNT).fill(startingTeam),
    ...Array(OTHER_TEAM_COUNT).fill(otherTeam),
    ...Array(NEUTRAL_COUNT).fill("neutral"),
    "trap",
  ];
}

function createCandidate(
  board: string[],
  teamTemplate: CardTeam[],
  random: RandomFn
): AssignedCard[] {
  const shuffledTeams = shuffleWithRandom(teamTemplate, random);
  return board.map((word, index) => ({
    word,
    team: shuffledTeams[index],
  }));
}

function nudgeTrapTowardCluster(
  cards: AssignedCard[],
  startingTeam: TeamColor,
  otherTeam: TeamColor,
  random: RandomFn,
  semantics: SemanticResolver
): AssignedCard[] {
  const trapIndex = cards.findIndex((card) => card.team === "trap");
  if (trapIndex < 0) return cards;

  const startingWords = cards
    .filter((card) => card.team === startingTeam)
    .map((card) => card.word);
  const otherWords = cards
    .filter((card) => card.team === otherTeam)
    .map((card) => card.word);

  const currentTrapSynergy = Math.max(
    semantics.countRelated(cards[trapIndex].word, startingWords),
    semantics.countRelated(cards[trapIndex].word, otherWords)
  );
  const familyDistributions = getFamilyDistributions(cards, semantics);
  const getTrapCoverageScore = (word: string): number => {
    const familyIds = semantics.getFamilyIds(word);
    if (familyIds.length === 0) return 0;

    let bestCoverage = 0;
    for (const familyId of familyIds) {
      const family = familyDistributions.get(familyId);
      if (!family) continue;
      const maxTeamCount = Math.max(family.red, family.blue);
      const minTeamCount = Math.min(family.red, family.blue);
      const dominance = maxTeamCount - minTeamCount;
      const coverage = maxTeamCount * 2 + dominance;
      if (coverage > bestCoverage) bestCoverage = coverage;
    }
    return bestCoverage;
  };
  const currentTrapCoverage = getTrapCoverageScore(cards[trapIndex].word);
  const currentTrapScore = currentTrapSynergy * 10 + currentTrapCoverage * 4;

  let bestScore = currentTrapScore;
  let bestNeutralIndices: number[] = [];

  cards.forEach((card, index) => {
    if (card.team !== "neutral") return;
    const synergy = Math.max(
      semantics.countRelated(card.word, startingWords),
      semantics.countRelated(card.word, otherWords)
    );
    const coverage = getTrapCoverageScore(card.word);
    const score = synergy * 10 + coverage * 4;
    if (score > bestScore) {
      bestScore = score;
      bestNeutralIndices = [index];
    } else if (score === bestScore && score > currentTrapScore) {
      bestNeutralIndices.push(index);
    }
  });

  if (bestNeutralIndices.length === 0 || bestScore <= currentTrapScore) {
    return cards;
  }

  const replacementTrapIndex =
    bestNeutralIndices[Math.floor(random() * bestNeutralIndices.length)];
  const adjusted = cards.map((card) => ({ ...card }));
  adjusted[trapIndex].team = "neutral";
  adjusted[replacementTrapIndex].team = "trap";
  return adjusted;
}

export function assignTeamsWithPlayabilityBias(
  board: string[],
  startingTeam: TeamColor,
  options: AssignmentOptions = {}
): AssignedCard[] {
  const random = options.random ?? Math.random;
  const attempts = options.attempts ?? ASSIGNMENT_SEARCH_ATTEMPTS;
  const semantics = getSemanticResolver(options.packs, options.semanticWords ?? []);
  const otherTeam: TeamColor = startingTeam === "red" ? "blue" : "red";
  const teamTemplate = createTeamTemplate(startingTeam);
  const bestCandidates: { cards: AssignedCard[]; quality: AssignmentQuality }[] = [];

  for (let attempt = 0; attempt < attempts; attempt++) {
    const cards = createCandidate(board, teamTemplate, random);
    const quality = evaluateAssignment(cards, startingTeam, otherTeam, random, semantics);

    bestCandidates.push({ cards, quality });
    bestCandidates.sort((a, b) => b.quality.score - a.quality.score);
    if (bestCandidates.length > TOP_ASSIGNMENT_POOL_SIZE) {
      bestCandidates.pop();
    }
  }

  if (bestCandidates.length === 0) {
    return createCandidate(board, teamTemplate, random);
  }

  const topSliceSize = Math.min(PICK_FROM_TOP_CANDIDATES, bestCandidates.length);
  const picked = bestCandidates[Math.floor(random() * topSliceSize)];
  return nudgeTrapTowardCluster(picked.cards, startingTeam, otherTeam, random, semantics);
}
